import { useCallback, useEffect, useState } from "react";

/** Chrome / Edge / Samsung Internet fire this before showing their own install UI. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/** True inside an installed PWA (home-screen launch), on every platform. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.("(display-mode: standalone)").matches === true || nav.standalone === true;
}

/** iPhone / iPad Safari has no install event: the user adds the page via Share → "Add to Home Screen". */
export function isIosSafari(ua: string = navigator.userAgent): boolean {
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export type InstallState =
  /** Nothing to offer: already installed, or the browser cannot install. */
  | { kind: "none" }
  /** The browser offered an install prompt we can trigger. */
  | { kind: "prompt"; install: () => Promise<boolean> }
  /** iOS Safari: only manual instructions. */
  | { kind: "ios" };

/**
 * What the "Instaliraj aplikaciju" entry should do. The deferred event is kept in
 * module scope so a prompt that fired before the shell mounted is not lost.
 */
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
}

export function useInstallPrompt(): InstallState {
  const [, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  const install = useCallback(async () => {
    const ev = deferred;
    if (!ev) return false;
    await ev.prompt();
    const { outcome } = await ev.userChoice;
    if (outcome === "accepted") deferred = null;
    listeners.forEach((l) => l());
    return outcome === "accepted";
  }, []);

  if (isStandalone()) return { kind: "none" };
  if (deferred) return { kind: "prompt", install };
  if (isIosSafari()) return { kind: "ios" };
  return { kind: "none" };
}
